import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn, renderDisplayText } from "@/lib/utils";
import { LoadingLabel, Skel } from "@/components/home/TenantSkeletons";
import { GoogleGlyph } from "@/components/reviews/GoogleGlyph";
import type { HeroModel, ReviewsModel, ServicesModel, TrustItem } from "@/components/home/layouts/models";
import {
  HeroActions, HeroCopySkeleton, HeroTitle, Photo, ReviewExtras, ReviewerLine, StarRow,
  type HeroViewProps,
} from "@/components/home/layouts/shared";

/** Thin ornamental rule with a diamond in the middle. */
export function Rule({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-4 text-primary/60", className)} aria-hidden>
      <span className="h-px flex-1 bg-current opacity-40" />
      <span className="h-1.5 w-1.5 rotate-45 border border-current" />
      <span className="h-px flex-1 bg-current opacity-40" />
    </div>
  );
}

/** Elegant Serif: editorial masthead, tall portrait with caption. */
export function ElegantHero({ m, edit }: HeroViewProps) {
  const seq = (n: number) => ({ ["--seq" as string]: n });
  return (
    <section className="v-elegant-hero relative pb-16 pt-14 md:pb-24 md:pt-16" aria-busy={!m.ready}>
      <div className="container mx-auto px-5 md:px-6">
        {m.ready ? (
          <div className="mx-auto max-w-4xl text-center">
            <p className="tp-eyebrow tp-seq" style={seq(0)}>{m.eyebrow}</p>
            <HeroTitle m={m} edit={edit} className="tp-display v-el-title tp-seq mt-8" />
            <Rule className="mx-auto mt-10 max-w-xs tp-seq" />
            <div className="tp-lead tp-seq mx-auto mt-8 max-w-2xl" style={seq(2)}>{edit?.subtitle ?? <p>{m.subtitle}</p>}</div>
            <div className="tp-seq mt-10 flex justify-center" style={seq(3)}><HeroActions m={m} edit={edit} className="items-center justify-center" /></div>
          </div>
        ) : (
          <HeroCopySkeleton align="center" className="mx-auto max-w-3xl" />
        )}

        <figure className="tp-hero-media mx-auto mt-14 grid max-w-5xl items-end gap-8 md:mt-20 md:grid-cols-[1fr_minmax(0,26rem)_1fr] md:gap-10">
          <div className="hidden pb-2 text-right md:block">
            <p className="tp-numeral text-6xl leading-none text-primary">{m.reviewsPending ? "–" : m.rating.toFixed(1)}</p>
            <StarRow className="mt-3 inline-flex text-sm" />
            <p className="mt-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">{m.ratingLabel}</p>
          </div>
          <div>
            <div className="relative aspect-[3/4] overflow-hidden bg-muted p-0 ring-1 ring-foreground/15 ring-offset-8 ring-offset-background">
              {m.heroPending ? <Skel className="absolute inset-0 h-full w-full rounded-none" /> : edit?.image ?? <Photo src={m.heroImage} alt="Professional cleaner at work" fetchPriority="high" decoding="async" />}
            </div>
            <figcaption className="mt-6 text-center text-sm italic text-muted-foreground">{m.areas.length > 0 ? `Serving ${m.areas.slice(0, 3).join(", ")}` : "Impeccable, every visit"}</figcaption>
          </div>
          <div className="hidden pb-2 md:block">
            {m.trust.slice(0, 2).map((t) => (
              <p key={t.key} className="mb-4 last:mb-0"><span className="block text-xs font-semibold uppercase tracking-[0.2em] text-foreground">{t.title}</span><span className="text-sm italic text-muted-foreground">{t.subtitle}</span></p>
            ))}
          </div>
        </figure>
      </div>
    </section>
  );
}

export function ElegantTrust({ items }: { m?: HeroModel; items: TrustItem[] }) {
  return (
    <section aria-label="Why customers trust us" className="border-y border-foreground/20 py-5">
      <div className="container mx-auto px-5 md:px-6">
        <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-center text-xs font-semibold uppercase tracking-[0.22em] text-foreground">
          {items.map((it, i) => (
            <li key={it.key} className="flex items-center gap-8">
              {i > 0 && <span className="hidden h-1.5 w-1.5 rotate-45 border border-primary sm:block" aria-hidden />}
              <span>{it.title}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function ElegantServices({ s }: { s: ServicesModel }) {
  return (
    <section className="tp-section">
      <div className="container mx-auto px-5 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="tp-eyebrow" data-reveal>The menu</p>
          <h2 className="tp-h2 mt-5" data-reveal>{renderDisplayText(s.title)}</h2>
          <p className="tp-lead mt-5 italic" data-reveal>{renderDisplayText(s.lead)}</p>
          <Rule className="mx-auto mt-8 max-w-[10rem]" />
        </div>
        {s.loading ? (
          <div className="tp-skeleton mx-auto mt-14 max-w-5xl space-y-6" aria-busy="true"><LoadingLabel>Loading services</LoadingLabel>{[0, 1, 2].map((i) => <Skel key={i} className="h-10 w-full tp-skel-round" />)}</div>
        ) : (
          <div className="mx-auto mt-14 grid max-w-5xl items-start gap-x-16 gap-y-12 md:mt-16 md:grid-cols-2">
            {s.items.map((item, i) => (
              <Link key={item.key} to={s.servicesHref} className="group block" data-reveal style={{ ["--reveal-delay" as string]: `${(i % 2) * 90}ms` }}>
                <div className="relative mb-6 aspect-[16/9] overflow-hidden bg-muted"><Photo src={item.image} alt={item.category} className="tp-zoom" loading="lazy" /></div>
                <div className="flex items-baseline gap-3">
                  <h3 className="tp-h3 shrink-0 !text-[1.35rem] group-hover:text-primary">{renderDisplayText(item.title)}</h3>
                  <span className="mb-1 min-w-4 flex-1 self-end border-b-2 border-dotted border-foreground/35" aria-hidden />
                  <span className="shrink-0 font-display text-lg text-primary">{item.price}</span>
                </div>
                {item.description && <p className="mt-2 pr-8 text-sm italic leading-relaxed text-muted-foreground">{item.description}</p>}
              </Link>
            ))}
          </div>
        )}
        <div className="mt-14 text-center"><Link to={s.servicesHref} className="tp-link text-sm">View the full menu <ArrowRight className="h-4 w-4" /></Link></div>
      </div>
    </section>
  );
}

export function ElegantReviews({ r, reviewFormHref }: { r: ReviewsModel; reviewFormHref?: string }) {
  return (
    <section className="tp-section tp-tint" aria-busy={r.pending}>
      <div className="container mx-auto px-5 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="tp-eyebrow" data-reveal>Reviews</p>
          <h2 className="tp-h2 mt-5" data-reveal>What our customers say</h2>
          {!r.pending && <p className="mt-5 text-sm uppercase tracking-[0.2em] text-muted-foreground">{r.average.toFixed(1)} · {r.label} · {r.countLine}</p>}
        </div>
        {r.pending ? (
          <div className="tp-skeleton mx-auto mt-12 max-w-3xl space-y-4" aria-hidden><LoadingLabel>Loading reviews</LoadingLabel><Skel className="h-6 w-full tp-skel-round" /><Skel className="h-6 w-3/4 tp-skel-round" /></div>
        ) : (
          <div className="mx-auto mt-14 grid max-w-5xl gap-x-16 gap-y-14 md:grid-cols-2">
            {r.items.map((item, i) => (
              <figure key={item.key} className={cn("text-center", i === 0 && r.items.length % 2 === 1 && "md:col-span-2 md:mx-auto md:max-w-2xl")} data-reveal data-testid={item.google ? "google-review-card" : undefined}>
                <span className="font-display text-6xl leading-none text-primary/40" aria-hidden>&ldquo;</span>
                <blockquote><p className="line-clamp-6 font-display text-xl italic leading-relaxed text-foreground md:text-[1.4rem]">{item.quote}</p></blockquote>
                <Rule className="mx-auto my-6 max-w-[6rem]" />
                <figcaption className="flex flex-col items-center gap-1">
                  <StarRow n={item.rating} className="text-xs" />
                  <div className="mt-1 text-center [&_p]:text-center"><ReviewerLine item={item} nameClassName="uppercase tracking-[0.18em] text-xs" /></div>
                  {item.google && <GoogleGlyph className="mt-1 h-4 w-4" />}
                </figcaption>
              </figure>
            ))}
          </div>
        )}
        {!r.pending && <ReviewExtras google={r.google} reviewFormHref={reviewFormHref} className="mt-12 justify-center" />}
      </div>
    </section>
  );
}
